import React from "react";
import {
  Alert,
  Box,
  ButtonBase,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SchedulingPreviewCalendar from "./SchedulingPreviewCalendar";
import { matchNumber, matchStage } from "components/common/matchIdentity";

export function summarizePreview(previewResult) {
  const scheduled = previewResult.scheduled_count ?? previewResult.assignments?.length ?? 0;
  const unscheduled = previewResult.unscheduled_count ?? previewResult.unscheduled?.length ?? 0;
  const total = previewResult.total_matches ?? scheduled + unscheduled;

  return { scheduled, unscheduled, total };
}

function SchedulingResultsSection({ editingDisabled = false, intl, onEditMatch, previewResult }) {
  if (!previewResult) return null;

  const summary = summarizePreview(previewResult);

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <SummaryCard
          eyebrow={intl.formatMessage({ id: "scheduling.review.assigned" })}
          value={`${summary.scheduled} / ${summary.total}`}
          detail={intl.formatMessage({ id: "scheduling.review.assignedHelp" })}
          tone={summary.unscheduled === 0 ? "success.main" : "text.primary"}
        />
        <SummaryCard
          eyebrow={intl.formatMessage({ id: "scheduling.review.unassigned" })}
          value={summary.unscheduled}
          detail={intl.formatMessage({
            id: summary.unscheduled > 0 ? "scheduling.review.unassignedAttention" : "scheduling.review.unassignedNone",
          })}
          tone={summary.unscheduled > 0 ? "warning.main" : "success.main"}
        />
      </Box>

      {previewResult.calendar?.resources?.length > 0 && (
        <Card variant="outlined" sx={{ minWidth: 0 }}>
          <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, "&:last-child": { pb: { xs: 1.5, md: 2.5 } } }}>
            <Typography variant="h5">{intl.formatMessage({ id: "scheduling.review.calendar" })}</Typography>
            <SchedulingPreviewCalendar result={previewResult} />
          </CardContent>
        </Card>
      )}

      <UnassignedMatches
        editingDisabled={editingDisabled}
        intl={intl}
        onEditMatch={onEditMatch}
        previewResult={previewResult}
        summary={summary}
      />
    </Stack>
  );
}

function SummaryCard({ eyebrow, value, detail, tone }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary">
          {eyebrow}
        </Typography>
        <Typography variant="h4" color={tone}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      </CardContent>
    </Card>
  );
}

function UnassignedMatches({ editingDisabled, intl, onEditMatch, previewResult, summary }) {
  const unscheduledMatches = previewResult.unscheduled || [];
  const draftApplied = previewResult.draft?.status === "applied";

  return (
    <Card variant="outlined" sx={{ borderColor: summary.unscheduled > 0 ? "warning.main" : undefined }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box>
            <Typography variant="overline" color={summary.unscheduled > 0 ? "warning.main" : "primary.main"}>
              {intl.formatMessage({ id: "scheduling.review.exceptions" })}
            </Typography>
            <Typography variant="h6">
              {intl.formatMessage({ id: "scheduling.review.unassignedCount" }, { count: summary.unscheduled })}
            </Typography>
          </Box>
          {summary.unscheduled > 0 && <WarningAmberOutlinedIcon color="warning" />}
        </Stack>
        <Divider sx={{ my: 2 }} />

        {summary.unscheduled === 0 ? (
          <Alert severity="success">{intl.formatMessage({ id: "scheduling.review.allMatchesAssigned" })}</Alert>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {intl.formatMessage({ id: "scheduling.review.partialConsequence" }, { count: summary.unscheduled })}
            </Alert>
            {unscheduledMatches.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {intl.formatMessage({
                    id: draftApplied
                      ? "scheduling.review.manualAssignmentHelp"
                      : "scheduling.review.manualAssignmentDraftHelp",
                  })}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.25,
                  }}
                >
                  {unscheduledMatches.map((unscheduledMatch) => (
                    <ButtonBase
                      key={unscheduledMatch.match_id}
                      aria-label={intl.formatMessage(
                        { id: "scheduling.review.assignMatch" },
                        { match: matchNumber(unscheduledMatch, (id, values) => intl.formatMessage({ id }, values)) },
                      )}
                      disabled={editingDisabled || !onEditMatch}
                      onClick={() => onEditMatch(unscheduledMatch.match_id)}
                      sx={{
                        p: 1.5,
                        width: "100%",
                        alignItems: "stretch",
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        textAlign: "left",
                        transition: "border-color 150ms ease, background-color 150ms ease",
                        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
                        <EditCalendarOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            spacing={0.5}
                          >
                            <Typography variant="subtitle2">
                              {matchNumber(unscheduledMatch, (id, values) => intl.formatMessage({ id }, values))}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {matchStage(unscheduledMatch, (id, values) => intl.formatMessage({ id }, values))}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {unscheduledMatch.tournament_name}
                          </Typography>
                          {unscheduledMatch.observed_constraints?.length > 0 && (
                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                              <Typography variant="caption" color="warning.main">
                                {intl.formatMessage({ id: "scheduling.review.reportedConstraints" })}
                              </Typography>
                              {unscheduledMatch.observed_constraints.map((code) => (
                                <Typography key={code} variant="body2">
                                  {formatConstraint(code, intl)}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </Box>
                        <ArrowForwardIcon color="action" fontSize="small" sx={{ mt: 0.25 }} />
                      </Stack>
                    </ButtonBase>
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function formatConstraint(code, intl) {
  if (code === "rest_equity") return intl.formatMessage({ id: "scheduling.equity.discarded" });
  const messageId = `scheduling.proposal.${code}`;
  return intl.messages?.[messageId] ? intl.formatMessage({ id: messageId }) : code;
}

export default SchedulingResultsSection;
